// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SystemBuilderProductInventroy from "@plumberssupply/App/Components/SystemBuilder/SystemBuilderProductInventroy";
import { CustomAppState, SystemBuilderProduct } from "@plumberssupply/App/Store/Reducers";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities";
import React, { FC, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

interface OwnProps {
    extendedStyles?: SystemBuilderProductCardStyles;
    product: SystemBuilderProduct;
}

export interface SystemBuilderProductCardStyles {
    cardWrapper?: InjectableCss;
    image?: LazyImageProps;
    productTitle?: TypographyPresentationProps;
    dimensions?: SmallHeadingAndTextStyles;
    imageWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    attributeLabelAndValues?: SmallHeadingAndTextStyles;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    productCardInfo: state.xSystemBuilder.productsInfo?.[ownProps?.product.id],
});

const mapDispatchToProps = {};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const systemBuilderProductCardStyles: SystemBuilderProductCardStyles = {};

const SystemBuilderProductCard: FC<Props> = ({ product, extendedStyles, productCardInfo }) => {
    const [styles] = useState(() => mergeWithCss(systemBuilderProductCardStyles, extendedStyles));

    const availability =
        productCardInfo?.inventory?.inventoryAvailabilityDtos?.find(
            o => o?.unitOfMeasure?.toLowerCase() === (productCardInfo?.unitOfMeasure?.toLowerCase() || ""),
        )?.availability || undefined;

    return (
        <StyledWrapper {...styles.cardWrapper}>
            <StyledWrapper {...styles.imageWrapper}>
                <LazyImage src={product.smallImagePath} />
            </StyledWrapper>
            <StyledWrapper {...styles.infoWrapper}>
                <Typography {...styles.productTitle}>{product.productTitle}</Typography>
                {product.productAttributeTypes.length > 0 &&
                    product.productAttributeTypes.map((attribute, index) => (
                        <SmallHeadingAndText
                            key={`${index}_attribute`}
                            heading={attribute.label}
                            text={attribute.values.join(", ")}
                            extendedStyles={styles.attributeLabelAndValues}
                        />
                    ))}
                <SystemBuilderProductInventroy
                    failedToLoadInventory={productCardInfo?.failedToLoadInventory}
                    unitOfMeasure={productCardInfo?.unitOfMeasure || ""}
                    productId={product.id}
                    availability={availability}
                />
                {product.qty > 1 && (
                    <SmallHeadingAndText
                        heading={translate("Qty")}
                        text={product.qty}
                        extendedStyles={styles.attributeLabelAndValues}
                    />
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SystemBuilderProductCard);
